import React, { useEffect, useMemo, useState } from 'react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { PERMISSIONS } from '../lib/constants';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Plus, Search, Loader2, Pencil, Trash2, Eye, Receipt, CheckCircle2, AlertTriangle, Wallet, Calendar, MapPin, Clock } from 'lucide-react';
import { StatusBadge } from '../components/StatusBadge';
import { EmptyState } from '../components/EmptyState';
import { formatIDR, formatIDRCompact, formatDateID } from '../lib/format';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { InvoiceFormDialog } from '../components/InvoiceFormDialog';
import { AdvancedFilterBar } from '../components/AdvancedFilterBar';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';

const STATUS_TABS = [
  { value: 'all', label: 'Semua' },
  { value: 'Draft', label: 'Draft' },
  { value: 'Sent', label: 'Terkirim' },
  { value: 'DP Paid', label: 'DP Dibayar' },
  { value: 'Partial Paid', label: 'Sebagian' },
  { value: 'Paid', label: 'Lunas' },
  { value: 'Overdue', label: 'Lewat Tempo' },
  { value: 'Cancelled', label: 'Dibatalkan' },
  { value: 'Dispute', label: 'Dispute' },
];

const SummaryTile = ({ label, value, accent = 'default', icon: Icon, testid }) => {
  const tones = {
    default: 'bg-card border-border text-foreground',
    primary: 'bg-primary/10 border-primary/30 text-primary',
    success: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300',
    blue: 'bg-blue-500/10 border-blue-500/30 text-blue-300',
    danger: 'bg-red-500/10 border-red-500/30 text-red-300',
    warn: 'bg-orange-500/10 border-orange-500/30 text-orange-300',
    muted: 'bg-white/5 border-white/10 text-muted-foreground',
  };
  return (
    <Card data-testid={testid} className={`shrink-0 min-w-[150px] sm:min-w-0 ${tones[accent]} border`}>
      <CardContent className="p-3 sm:p-4">
        <div className="flex items-center justify-between gap-2">
          <div className="text-[11px] sm:text-xs font-medium opacity-80 uppercase tracking-wide">{label}</div>
          {Icon ? <Icon className="h-4 w-4 opacity-70" /> : null}
        </div>
        <div className="mt-1.5 text-lg sm:text-xl font-semibold font-heading tracking-tight font-mono-num">
          {value}
        </div>
      </CardContent>
    </Card>
  );
};

const TaxBadges = ({ invoice, compact = false }) => {
  if (!invoice?.include_ppn && !invoice?.include_withholding_tax) return null;
  const size = compact ? 'text-[9px] px-1.5' : 'text-[10px] px-2';
  return (
    <span className="inline-flex items-center gap-1">
      {invoice.include_ppn && (
        <span className={`inline-flex items-center rounded-full border border-primary/30 bg-primary/10 text-primary py-0.5 font-medium ${size}`} data-testid={`invoices-ppn-badge-${invoice.id}`}>
          PPN
        </span>
      )}
      {invoice.include_withholding_tax && (
        <span className={`inline-flex items-center rounded-full border border-amber-400/30 bg-amber-400/10 text-amber-300 py-0.5 font-medium ${size}`} data-testid={`invoices-pph-badge-${invoice.id}`}>
          {invoice.withholding_tax_type || 'PPh'} {Number(invoice.withholding_tax_rate || 0)}%
        </span>
      )}
    </span>
  );
};

export default function Invoices() {
  const { role } = useAuth();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [allList, setAllList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    venue: '',
    event_date_from: '',
    event_date_to: '',
  });
  const [statusTab, setStatusTab] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.search) params.search = filters.search;
      if (statusTab !== 'all') params.status = statusTab;
      else if (filters.status) params.status = filters.status;
      if (filters.venue) params.venue = filters.venue;
      if (filters.event_date_from) params.event_date_from = filters.event_date_from;
      if (filters.event_date_to) params.event_date_to = filters.event_date_to;
      const { data } = await api.get('/invoices', { params });
      setList(data);
    } finally { setLoading(false); }
  };

  const loadAll = async () => {
    try {
      const { data } = await api.get('/invoices');
      setAllList(data);
    } catch (err) {
      // KPI summary is best-effort. Logging makes silent failures visible.
      console.error('[Invoices] failed to load KPI summary list', err);
    }
  };

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [filters, statusTab]);
  useEffect(() => { loadAll(); }, []);

  const kpis = useMemo(() => {
    let total = 0, paid = 0, outstanding = 0;
    let cntPaid = 0, cntOverdue = 0, cntPartial = 0;
    allList.forEach(i => {
      total += Number(i.invoice_amount) || 0;
      paid += Number(i.paid_amount) || 0;
      outstanding += Number(i.outstanding_amount) || 0;
      if (i.status === 'Paid') cntPaid += 1;
      if (i.status === 'Overdue') cntOverdue += 1;
      if (i.status === 'Partial Paid' || i.status === 'DP Paid') cntPartial += 1;
    });
    return { count: allList.length, total, paid, outstanding, cntPaid, cntOverdue, cntPartial };
  }, [allList]);

  const openNew = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (i) => { setEditing(i); setDialogOpen(true); };

  const remove = async (i) => {
    try {
      await api.delete(`/invoices/${i.id}`);
      toast.success('Invoice dihapus');
      load();
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Gagal menghapus');
    }
  };

  const onSaved = () => { load(); loadAll(); };

  const canEdit = PERMISSIONS.canCreateInvoice(role);
  const canDelete = PERMISSIONS.canDeleteFinancial(role);

  return (
    <div className="space-y-4" data-testid="invoices-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-heading font-semibold tracking-tight">Invoice</h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">Pantau invoice, pembayaran, dan piutang aktif</p>
        </div>
        {canEdit && (
          <Button onClick={openNew} className="h-10 sm:h-9" data-testid="invoices-add-button">
            <Plus className="h-4 w-4 mr-2" /> Buat Invoice
          </Button>
        )}
      </div>

      {/* KPI Summary */}
      <div
        className="-mx-4 px-4 flex gap-3 overflow-x-auto pb-2 sm:mx-0 sm:px-0 sm:grid sm:grid-cols-2 md:grid-cols-4 sm:gap-3 sm:overflow-visible"
        data-testid="invoices-kpi-strip"
      >
        <SummaryTile label="Total Invoice" value={kpis.count} icon={Receipt} testid="invoices-kpi-count" />
        <SummaryTile label="Total Tagihan" value={formatIDRCompact(kpis.total)} accent="primary" icon={Wallet} testid="invoices-kpi-total" />
        <SummaryTile label="Sudah Dibayar" value={formatIDRCompact(kpis.paid)} accent="success" icon={CheckCircle2} testid="invoices-kpi-paid" />
        <SummaryTile label="Outstanding" value={formatIDRCompact(kpis.outstanding)} accent="warn" icon={AlertTriangle} testid="invoices-kpi-outstanding" />
        <SummaryTile label="Lunas" value={kpis.cntPaid} accent="success" testid="invoices-kpi-cnt-paid" />
        <SummaryTile label="Lewat Tempo" value={kpis.cntOverdue} accent="danger" testid="invoices-kpi-cnt-overdue" />
        <SummaryTile label="Sebagian / DP" value={kpis.cntPartial} accent="blue" testid="invoices-kpi-cnt-partial" />
      </div>

      {/* Search + Advanced Filter Bar + Filter Pills */}
      <div className="space-y-3">
        <AdvancedFilterBar
          value={filters}
          onChange={(patch) => setFilters(prev => ({ ...prev, ...patch }))}
          onClear={() => { setFilters({ search: '', status: '', venue: '', event_date_from: '', event_date_to: '' }); setStatusTab('all'); }}
          searchPlaceholder="Cari nomor invoice, client, venue, atau deskripsi event"
          testidPrefix="invoices-filter"
        />
        <div
          className="-mx-4 px-4 flex gap-2 overflow-x-auto sm:mx-0 sm:px-0 sm:flex-wrap pb-1"
          data-testid="invoices-filter-pills"
          role="tablist"
        >
          {STATUS_TABS.map((t) => {
            const active = statusTab === t.value;
            return (
              <button
                type="button"
                role="tab"
                key={t.value}
                onClick={() => setStatusTab(t.value)}
                data-testid={`invoices-filter-${t.value}`}
                className={`shrink-0 inline-flex items-center h-9 px-3.5 rounded-full text-sm font-medium border transition-colors ${
                  active
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-card text-muted-foreground border-border hover:text-foreground hover:bg-white/5'
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* DESKTOP Table */}
      <Card className="hidden md:block bg-card border-border">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table data-testid="invoices-table">
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead>No</TableHead>
                  <TableHead>Tanggal</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Event</TableHead>
                  <TableHead>Jatuh Tempo</TableHead>
                  <TableHead className="text-right">Jumlah</TableHead>
                  <TableHead className="text-right">Terbayar</TableHead>
                  <TableHead className="text-right">Sisa</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && <TableRow><TableCell colSpan={10} className="text-center py-10 text-muted-foreground"><Loader2 className="inline h-4 w-4 animate-spin mr-2" />Memuat…</TableCell></TableRow>}
                {!loading && list.length === 0 && (
                  <TableRow><TableCell colSpan={10} className="p-0">
                    <EmptyState
                      icon={Receipt}
                      title="Belum ada invoice"
                      description="Buat invoice dari penawaran yang sudah disetujui."
                      action={canEdit ? <Button onClick={openNew}><Plus className="h-4 w-4 mr-2" />Buat Invoice</Button> : null}
                    />
                  </TableCell></TableRow>
                )}
                {!loading && list.map(i => (
                  <TableRow key={i.id} className="hover:bg-white/4 border-border" data-testid={`invoices-row-${i.id}`}>
                    <TableCell className="font-mono-num text-xs">{i.invoice_number}</TableCell>
                    <TableCell className="text-muted-foreground">{formatDateID(i.invoice_date)}</TableCell>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span>{i.client_name}</span>
                        <TaxBadges invoice={i} />
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground truncate max-w-[200px]">{i.event_description}</TableCell>
                    <TableCell className="text-muted-foreground">{formatDateID(i.due_date)}</TableCell>
                    <TableCell className="text-right font-mono-num">{formatIDR(i.invoice_amount)}</TableCell>
                    <TableCell className="text-right font-mono-num text-emerald-300">{formatIDR(i.paid_amount)}</TableCell>
                    <TableCell className="text-right font-mono-num">{formatIDR(i.outstanding_amount)}</TableCell>
                    <TableCell><StatusBadge status={i.status} /></TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(`/invoices/${i.id}`)} data-testid={`invoices-view-${i.id}`}><Eye className="h-4 w-4" /></Button>
                      {canEdit && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(i)}><Pencil className="h-4 w-4" /></Button>}
                      {canDelete && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8 text-red-300" data-testid={`invoices-delete-${i.id}`}><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader><AlertDialogTitle>Hapus invoice?</AlertDialogTitle><AlertDialogDescription>Semua pembayaran terkait juga akan dihapus. Aksi ini tidak dapat dibatalkan.</AlertDialogDescription></AlertDialogHeader>
                            <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction onClick={() => remove(i)}>Hapus</AlertDialogAction></AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* MOBILE Cards */}
      <div className="md:hidden space-y-3" data-testid="invoices-mobile-list">
        {loading && (
          <div className="text-center py-10 text-muted-foreground">
            <Loader2 className="inline h-4 w-4 animate-spin mr-2" />Memuat…
          </div>
        )}
        {!loading && list.length === 0 && (
          <Card className="bg-card border-border">
            <CardContent className="p-0">
              <EmptyState
                icon={Receipt}
                title="Belum ada invoice"
                description="Buat invoice dari penawaran yang sudah disetujui."
                action={canEdit ? <Button onClick={openNew}><Plus className="h-4 w-4 mr-2" />Buat Invoice</Button> : null}
              />
            </CardContent>
          </Card>
        )}
        {!loading && list.map((i) => (
          <Card
            key={i.id}
            className="bg-card border-border active:scale-[0.99] transition-transform"
            data-testid={`invoices-card-${i.id}`}
          >
            <CardContent className="p-4 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <button
                    type="button"
                    onClick={() => navigate(`/invoices/${i.id}`)}
                    className="font-mono-num text-xs text-primary hover:underline"
                  >
                    {i.invoice_number}
                  </button>
                  <div className="font-semibold mt-0.5 truncate">{i.client_name}</div>
                  <div className="mt-1"><TaxBadges invoice={i} compact /></div>
                </div>
                <StatusBadge status={i.status} />
              </div>

              {i.event_description ? (
                <div className="text-sm text-foreground/90 line-clamp-2">{i.event_description}</div>
              ) : null}

              <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                <div className="flex items-center gap-1.5 min-w-0">
                  <Calendar className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">{formatDateID(i.invoice_date)}</span>
                </div>
                {i.due_date ? (
                  <div className="flex items-center gap-1.5 min-w-0">
                    <Clock className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">Jatuh tempo {formatDateID(i.due_date)}</span>
                  </div>
                ) : null}
                {i.venue ? (
                  <div className="flex items-center gap-1.5 min-w-0 col-span-2">
                    <MapPin className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">{i.venue}</span>
                  </div>
                ) : null}
              </div>

              <div className="rounded-lg bg-white/5 border border-white/10 p-3 grid grid-cols-3 gap-2 text-xs">
                <div>
                  <div className="text-muted-foreground">Total</div>
                  <div className="font-mono-num font-semibold mt-0.5">{formatIDR(i.invoice_amount)}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Terbayar</div>
                  <div className="font-mono-num font-semibold text-emerald-300 mt-0.5">{formatIDR(i.paid_amount)}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Sisa</div>
                  <div className="font-mono-num font-semibold text-orange-300 mt-0.5">{formatIDR(i.outstanding_amount)}</div>
                </div>
              </div>

              <div className="flex items-center gap-2 pt-1">
                <Button
                  type="button"
                  variant="secondary"
                  className="flex-1 h-10"
                  onClick={() => navigate(`/invoices/${i.id}`)}
                  data-testid={`invoices-card-view-${i.id}`}
                >
                  <Eye className="h-4 w-4 mr-1.5" />Detail
                </Button>
                {canEdit && (
                  <Button
                    type="button"
                    variant="secondary"
                    className="h-10 w-10 shrink-0"
                    size="icon"
                    onClick={() => openEdit(i)}
                    aria-label="Edit"
                    data-testid={`invoices-card-edit-${i.id}`}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                )}
                {canDelete && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        type="button"
                        variant="secondary"
                        className="h-10 w-10 shrink-0 text-red-300 hover:text-red-200"
                        size="icon"
                        aria-label="Hapus"
                        data-testid={`invoices-card-delete-${i.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Hapus invoice?</AlertDialogTitle>
                        <AlertDialogDescription>Semua pembayaran terkait juga akan dihapus. Aksi ini tidak dapat dibatalkan.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Batal</AlertDialogCancel>
                        <AlertDialogAction onClick={() => remove(i)}>Hapus</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <InvoiceFormDialog open={dialogOpen} onOpenChange={setDialogOpen} editing={editing} onSaved={onSaved} />
    </div>
  );
}
